import { BayLevelEnum, VesselPartTypeEnum, } from "open-vessel-definition";
import { ONE_MILLIMETER_IN_FEET, createDictionary, feetToMillimeters, roundDec, } from "@tedivo/tedivo-pure-helpers";
/**
 * Calculates BaySlots for VesselOne using bridges, smkoe-stack, cranes, spacers and bays
 * @param parts
 * @param bayLcgVcgTcgAndPairings
 * @param bls BayData[], required to calculate lashing bridges and bulkheads
 * @returns
 */
export function calculatePartsForVesselOne(parts, bayLcgVcgTcgAndPairings, bls) {
    const partsWithCalcs = [];
    const { maxLcg, lcgsBy20Bay, bayLevelPositionsDict } = bayLcgVcgTcgAndPairings;
    const blsDict = createDictionary(bls, (b) => `${b.isoBay}-${b.level}`);
    const lenToVOne = (len) => len * ONE_MILLIMETER_IN_FEET;
    const firstBayIndexInCalculatedSlots = parts.findIndex((b) => typeof b === "string");
    const lenOfInitialParts = firstBayIndexInCalculatedSlots === 0
        ? 0
        : parts.slice(0, firstBayIndexInCalculatedSlots).reduce((acc, part) => {
            if (typeof part !== "object")
                return acc;
            return acc + part.len;
        }, 0);
    const maxLcgFore = roundDec(maxLcg + lenOfInitialParts, 4);
    const posX = (f) => ((maxLcgFore - f) / 100) * ONE_MILLIMETER_IN_FEET;
    let lastPositionXAft = 0;
    let lastAftLcg = maxLcgFore;
    const processed = [];
    parts.forEach((b) => {
        var _a, _b, _c, _d;
        // If it's a string, it's a bay
        if (typeof b === "string" && !isNaN(Number(b))) {
            const bay = b;
            if (processed.includes(bay))
                return;
            const pairedBay = lcgsBy20Bay[bay].confirmedPairedBay;
            const maxSize = getMaxSizeOfAllBaysAndLevels(bay, pairedBay);
            const id = pairedBay ? `${bay}-${pairedBay}` : bay;
            const calcLen = maxSize === -Infinity ? (pairedBay ? 40 : 20) : undefined;
            const foreLcgs = [lcgsBy20Bay[bay].maxForeLcg];
            if (pairedBay)
                foreLcgs.push(lcgsBy20Bay[pairedBay].maxForeLcg);
            const lashingBridges = ((_a = blsDict[`${bay}-${BayLevelEnum.ABOVE}`]) === null || _a === void 0 ? void 0 : _a.lashingBridges) ||
                (pairedBay && ((_b = blsDict[`${pairedBay}-${BayLevelEnum.ABOVE}`]) === null || _b === void 0 ? void 0 : _b.lashingBridges)) ||
                undefined;
            const bulkheads = ((_c = blsDict[`${bay}-${BayLevelEnum.BELOW}`]) === null || _c === void 0 ? void 0 : _c.bulkhead) ||
                (pairedBay && ((_d = blsDict[`${pairedBay}-${BayLevelEnum.BELOW}`]) === null || _d === void 0 ? void 0 : _d.bulkhead)) ||
                undefined;
            const blsInfo = [bay, pairedBay].filter(Boolean).map((b) => {
                var _a;
                return ({
                    bay: b,
                    pairedDir: (_a = lcgsBy20Bay === null || lcgsBy20Bay === void 0 ? void 0 : lcgsBy20Bay[b]) === null || _a === void 0 ? void 0 : _a.paired,
                });
            });
            const maxForeLcg = Math.max(...foreLcgs);
            const positionX = posX(maxForeLcg);
            partsWithCalcs.push({
                id,
                type: VesselPartTypeEnum.BAY,
                blsInfo,
                label: id,
                lashingBridges,
                bulkheads,
                len: maxSize,
                calcLen,
                maxSize,
                positionX,
                fwdLcg: maxForeLcg,
                aftLcg: maxForeLcg - feetToMillimeters(maxSize),
            });
            lastPositionXAft = positionX + maxSize / 100;
            lastAftLcg = maxForeLcg - feetToMillimeters(maxSize);
            processed.push(bay);
            if (pairedBay)
                processed.push(pairedBay);
            return;
        }
        if (typeof b !== "object")
            return;
        let len = 0;
        switch (b.type) {
            case VesselPartTypeEnum.CRANE:
                len = lenToVOne(b.len || 10000);
                partsWithCalcs.push({
                    id: b.id,
                    type: VesselPartTypeEnum.CRANE,
                    label: b.label,
                    cranes: b.cranes,
                    len,
                    positionX: lastPositionXAft,
                    fwdLcg: lastAftLcg,
                    aftLcg: lastAftLcg - feetToMillimeters(len),
                    original: b,
                });
                break;
            case VesselPartTypeEnum.BRIDGE:
                len = lenToVOne(b.len || 40000);
                partsWithCalcs.push({
                    id: b.id,
                    type: VesselPartTypeEnum.BRIDGE,
                    label: b.label,
                    heatSrcBelow: b.heatSrcBelow,
                    len,
                    positionX: lastPositionXAft,
                    fwdLcg: lastAftLcg,
                    aftLcg: lastAftLcg - feetToMillimeters(len),
                    original: b,
                });
                break;
            case VesselPartTypeEnum.SMOKE:
                len = lenToVOne(b.len || 20000);
                partsWithCalcs.push({
                    id: b.id,
                    type: VesselPartTypeEnum.SMOKE,
                    label: b.label,
                    numberOfSmokeStacks: b.numberOfSmokeStacks,
                    len,
                    positionX: lastPositionXAft,
                    fwdLcg: lastAftLcg,
                    aftLcg: lastAftLcg - feetToMillimeters(len),
                    original: b,
                });
                break;
            case VesselPartTypeEnum.SPACER:
                len = lenToVOne(b.len || 0);
                partsWithCalcs.push({
                    id: b.id,
                    type: VesselPartTypeEnum.SPACER,
                    label: b.label,
                    len,
                    positionX: lastPositionXAft,
                    fwdLcg: lastAftLcg,
                    aftLcg: lastAftLcg - feetToMillimeters(len),
                    original: b,
                });
                break;
        }
        lastPositionXAft += len / 100;
        lastAftLcg -= feetToMillimeters(len);
    });
    return partsWithCalcs;
    function getMaxSizeOfAllBaysAndLevels(bay, paired) {
        var _a, _b, _c, _d;
        const baySizes = [
            ...(((_a = bayLevelPositionsDict[`${bay}-${BayLevelEnum.ABOVE}`]) === null || _a === void 0 ? void 0 : _a.sizes) || []),
            ...(((_b = bayLevelPositionsDict[`${bay}-${BayLevelEnum.BELOW}`]) === null || _b === void 0 ? void 0 : _b.sizes) || []),
        ];
        let pairedBaySizes = [];
        if (paired) {
            pairedBaySizes = [
                ...(((_c = bayLevelPositionsDict[`${paired}-${BayLevelEnum.ABOVE}`]) === null || _c === void 0 ? void 0 : _c.sizes) || []),
                ...(((_d = bayLevelPositionsDict[`${paired}-${BayLevelEnum.BELOW}`]) === null || _d === void 0 ? void 0 : _d.sizes) || []),
            ];
            baySizes.push(...pairedBaySizes);
        }
        const maxSize = Math.max(...baySizes);
        if (maxSize === 20 && pairedBaySizes.length > 0 && baySizes.length > 0) {
            return Math.max(...baySizes) + Math.max(...pairedBaySizes);
        }
        return maxSize;
    }
}
